/**
 * AIFLedger Cookie Banner — premium bottom-right slide-in.
 *
 * • Categorises cookies: Essential (always on) + Analytics + Marketing.
 * • Wires into Google Consent Mode v2 via `cookieConsent.js`.
 * • Persists preference for 12 months.
 * • "Cookie preferences" link elsewhere in the app can dispatch
 *   `window.dispatchEvent(new Event('aif-open-cookie-prefs'))` to reopen it.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { hasDecided, setConsent, getConsent, bootConsent } from "@/lib/cookieConsent";
import { Cookie, ShieldCheck } from "lucide-react";

const BORDER = "#e2e1dd";

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [mode, setMode] = useState("banner"); // "banner" | "prefs"
  const [analytics, setAnalytics] = useState(true);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    bootConsent();
    if (!hasDecided()) {
      const t = setTimeout(() => setVisible(true), 600);
      return () => clearTimeout(t);
    }
    return undefined;
  }, []);

  useEffect(() => {
    const open = () => {
      const c = getConsent();
      setAnalytics(c ? c.analytics : true);
      setMarketing(c ? c.marketing : false);
      setMode("prefs");
      setVisible(true);
    };
    window.addEventListener("aif-open-cookie-prefs", open);
    return () => window.removeEventListener("aif-open-cookie-prefs", open);
  }, []);

  if (!visible) return null;

  const acceptAll = () => {
    setConsent({ analytics: true, marketing: true });
    setVisible(false);
  };
  const rejectNonEssential = () => {
    setConsent({ analytics: false, marketing: false });
    setVisible(false);
  };
  const savePrefs = () => {
    setConsent({ analytics, marketing });
    setVisible(false);
  };

  return (
    <div className="fixed bottom-4 right-4 z-[60] max-w-[420px] w-[calc(100vw-2rem)]"
         data-testid="cookie-banner">
      <div className="bg-white rounded-xl shadow-2xl border overflow-hidden"
           style={{ borderColor: BORDER, boxShadow: "0 20px 50px rgba(11,25,44,0.18)" }}>
        <div className="px-5 pt-5 pb-3 flex items-start gap-3 border-b" style={{ borderColor: BORDER }}>
          <div className="w-10 h-10 rounded-lg bg-[#faf9f5] border flex items-center justify-center flex-shrink-0"
               style={{ borderColor: BORDER }}>
            <Cookie size={18} className="text-[#c4a454]" />
          </div>
          <div>
            <div className="font-serif text-lg text-[#0b192c] leading-tight">
              {mode === "banner" ? "Cookies & privacy" : "Cookie preferences"}
            </div>
            <div className="text-[11px] uppercase tracking-widest text-[#94a3b8] font-semibold mt-1">
              AIFLedger by CorpZo
            </div>
          </div>
        </div>

        {mode === "banner" ? (
          <div className="px-5 py-4">
            <p className="text-[13px] leading-relaxed text-[#334155]">
              We use <b>essential cookies</b> to keep AIFLedger secure and functional, and
              optional <b>analytics cookies</b> to understand how our platform is used so we can
              improve it. You may accept all or reject non-essential cookies.
            </p>
            <p className="text-[11px] leading-relaxed text-[#475569] mt-3">
              See our{" "}
              <Link to="/privacy" className="text-[#0b192c] font-semibold hover:text-[#c4a454] underline">
                Privacy Policy
              </Link>{" "}
              for details.
            </p>
            <div className="mt-4 flex flex-col sm:flex-row gap-2">
              <button onClick={rejectNonEssential}
                      className="flex-1 h-10 px-4 rounded-md border text-sm font-semibold text-[#0b192c] hover:bg-[#faf9f5] transition"
                      style={{ borderColor: BORDER }}
                      data-testid="cookie-reject-btn">
                Reject non-essential
              </button>
              <button onClick={acceptAll}
                      className="flex-1 h-10 px-4 rounded-md bg-[#0b192c] hover:bg-[#0b192c]/90 text-white text-sm font-semibold transition"
                      data-testid="cookie-accept-btn">
                Accept all
              </button>
            </div>
            <button onClick={() => setMode("prefs")}
                    className="w-full mt-2 h-8 text-xs uppercase tracking-wider text-[#475569] hover:text-[#0b192c] font-semibold"
                    data-testid="cookie-customize-btn">
              Customize preferences →
            </button>
          </div>
        ) : (
          <div className="px-5 py-4">
            <CategoryRow
              icon={<ShieldCheck size={14} className="text-[#166534]" />}
              title="Essential cookies"
              subtitle="Session, authentication, security. Required for AIFLedger to work."
              locked
            />
            <CategoryRow
              title="Analytics cookies"
              subtitle="Google Analytics 4, product analytics. Helps us improve the platform."
              value={analytics}
              onChange={setAnalytics}
              testId="cookie-toggle-analytics"
            />
            <CategoryRow
              title="Marketing cookies"
              subtitle="Reserved for future retargeting on the marketing site. Currently unused."
              value={marketing}
              onChange={setMarketing}
              testId="cookie-toggle-marketing"
            />
            <div className="mt-4 flex flex-col sm:flex-row gap-2">
              <button onClick={() => setMode("banner")}
                      className="flex-1 h-10 px-4 rounded-md border text-sm font-semibold text-[#0b192c] hover:bg-[#faf9f5] transition"
                      style={{ borderColor: BORDER }}
                      data-testid="cookie-prefs-back">
                Back
              </button>
              <button onClick={savePrefs}
                      className="flex-1 h-10 px-4 rounded-md bg-[#0b192c] hover:bg-[#0b192c]/90 text-white text-sm font-semibold transition"
                      data-testid="cookie-prefs-save">
                Save preferences
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CategoryRow({ icon, title, subtitle, value, onChange, locked, testId }) {
  return (
    <div className="py-3 border-b last:border-b-0 flex items-start gap-3" style={{ borderColor: BORDER }}>
      <div className="flex-1">
        <div className="text-sm font-semibold text-[#0b192c] flex items-center gap-2">
          {icon}
          {title}
        </div>
        <div className="text-[11px] text-[#475569] mt-1 leading-relaxed">{subtitle}</div>
      </div>
      {locked ? (
        <div className="text-[10px] uppercase tracking-widest font-semibold text-[#166534] flex-shrink-0 pt-1">
          Always on
        </div>
      ) : (
        <button onClick={() => onChange(!value)}
                className={`relative flex-shrink-0 mt-0.5 h-5 w-9 rounded-full transition ${value ? "bg-[#0b192c]" : "bg-[#cbd5e1]"}`}
                data-testid={testId}
                aria-pressed={value}>
          <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition ${value ? "left-[18px]" : "left-0.5"}`} />
        </button>
      )}
    </div>
  );
}
